/**
 * DashboardPage — ekran "Start".
 *
 * Wizualizacja postępu workflow PAX (5 kroków):
 *   1. KSeF skonfigurowany
 *   2. Zaimportowano faktury z KSeF
 *   3. Wgrano wyciąg bankowy
 *   4. Przeanalizowano kontrahentów
 *   5. Przygotowano paczki płatności
 *
 * Sposób wyliczania statusu (bez nowych endpointów / kolekcji):
 *   step 1  → useKsefStatus (GET /api/ksef/admin/config + /api/ksef/company-nip)
 *   step 2  → GET /api/invoices?limit=1 → `total > 0`
 *   step 3  → GET /api/bank-statements/imports?limit=1 → `total > 0`
 *   step 4  → GET /api/ai/agents/invoice/profiles/count → `total > 0`
 *              (fix 2026-02: dawniej `/agents/invoice/memory` → legacy
 *              kolekcja `agent_vendor_memory` (sirota, 0 rekordów). Nowy
 *              endpoint liczy `vendor_invoice_profiles`, do którego faktycznie
 *              pisze Agent Faktur. Patrz
 *              docs/PAX_START_STEP4_CONTRACTOR_PROFILE_SYNC_AUDIT.md).
 *   step 5  → moduł placeholder → zawsze PENDING
 *
 * Pierwszy krok bez statusu COMPLETED jest oznaczany jako CURRENT (niebieski).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Circle, ArrowRight } from "lucide-react";
import { useKsefStatus } from "@/components/layout/useKsefStatus";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS = {
  COMPLETED: "completed",
  CURRENT: "current",
  PENDING: "pending",
};

const STEP_DEFS = [
  {
    n: 1,
    actionTitle: "Konfiguracja KSeF",
    completedTitle: "KSeF skonfigurowany",
    description: "Połącz aplikację z KSeF przy użyciu NIP, certyfikatu i hasła.",
    path: "/ksef-config",
  },
  {
    n: 2,
    actionTitle: "Import faktur z KSeF",
    completedTitle: "Zaimportowano faktury z KSeF",
    description: "Pobierz faktury zakupowe z KSeF.",
    path: "/ksef-inbox",
  },
  {
    n: 3,
    actionTitle: "Import wyciągu bankowego",
    completedTitle: "Zaimportowano wyciąg bankowy",
    description: "Wgraj wyciąg bankowy do analizy płatności.",
    path: "/wyciagi",
  },
  {
    n: 4,
    actionTitle: "Przeanalizuj faktury",
    completedTitle: "Przeanalizowano faktury",
    description: "Odczytaj dane z faktur potrzebne do przygotowania płatności.",
    path: "/invoices",
  },
  {
    n: 5,
    actionTitle: "Przygotowanie paczek płatności",
    completedTitle: "Przygotowano paczki płatności",
    description: "Przygotuj propozycje paczek płatności.",
    path: "/paczki",
  },
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const ksef = useKsefStatus();
  const { user } = useAuth();
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [vendorMemoryCount, setVendorMemoryCount] = useState(0);
  const [bankImportCount, setBankImportCount] = useState(0);

  /**
   * Wyliczenie imienia użytkownika (frontend-only, bez nowych endpointów).
   *
   * Fallback chain:
   *   1. user.name (AuthContext.syncFirebaseUser ustawia: Firebase displayName || email-przed-@)
   *   2. user.email → część przed `@`
   *   3. brak → null (renderujemy "Witaj 👋")
   *
   * Bierzemy tylko pierwszy token (imię), żeby uniknąć "Witaj, Jan Kowalski 👋".
   */
  const rawName = (user?.name && String(user.name).trim()) || (user?.email ? String(user.email).split("@")[0] : "");
  const userName = rawName ? rawName.split(/\s+/)[0] : "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [invRes, memRes, bankRes] = await Promise.all([
          axios.get(`${API}/invoices?limit=1`, { withCredentials: true }).catch(() => ({ data: { total: 0 } })),
          axios.get(`${API}/ai/agents/invoice/profiles/count`, { withCredentials: true }).catch(() => ({ data: { total: 0 } })),
          axios.get(`${API}/bank-statements/imports?limit=1`, { withCredentials: true }).catch(() => ({ data: { total: 0 } })),
        ]);
        if (cancelled) return;
        setInvoiceCount(Number(invRes.data?.total || 0));
        setVendorMemoryCount(Number(memRes.data?.total || 0));
        setBankImportCount(Number(bankRes.data?.total || 0));
      } catch (_e) {
        // graceful fallback — kroki pozostaną PENDING
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Wyliczenie statusów kroków.
  const completedFlags = [
    ksef.configured,           // 1
    invoiceCount > 0,          // 2
    bankImportCount > 0,       // 3 — min. 1 zapisany import wyciągu
    vendorMemoryCount > 0,     // 4
    false,                     // 5 — placeholder
  ];

  const firstPendingIdx = completedFlags.findIndex((c) => !c);
  const statuses = completedFlags.map((completed, idx) => {
    if (completed) return STATUS.COMPLETED;
    if (idx === firstPendingIdx) return STATUS.CURRENT;
    return STATUS.PENDING;
  });

  return (
    <div data-testid="start-page" className="max-w-3xl mx-auto py-8 sm:py-12 px-4">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#0F1F3D]">Start</h1>
        <p
          className="text-sm sm:text-base text-gray-600 mt-3 max-w-xl leading-relaxed"
          data-testid="start-welcome"
        >
          {userName ? (
            <>
              Witaj, <span className="font-medium text-[#0F1F3D]">{userName}</span> <span aria-hidden="true">👋</span>
            </>
          ) : (
            <>
              Witaj <span aria-hidden="true">👋</span>
            </>
          )}
          <br />
          Do pierwszej gotowej paczki płatności pozostało jeszcze kilka kroków. Zacznij od aktualnego etapu oznaczonego na niebiesko.
        </p>
      </header>

      <ol className="space-y-3" data-testid="workflow-steps">
        {STEP_DEFS.map((step, idx) => {
          const status = statuses[idx];
          const isCompleted = status === STATUS.COMPLETED;
          const isCurrent = status === STATUS.CURRENT;

          const containerCls = [
            "group w-full text-left rounded-lg border bg-white px-4 py-4 sm:px-5 sm:py-4",
            "transition-colors flex items-start gap-4",
            isCurrent
              ? "border-blue-300 ring-1 ring-blue-200 hover:bg-blue-50/50"
              : "border-gray-200 hover:bg-gray-50",
          ].join(" ");

          const badgeCls = [
            "shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-semibold",
            isCompleted && "bg-emerald-50 text-emerald-700 border border-emerald-200",
            isCurrent && "bg-blue-600 text-white",
            !isCompleted && !isCurrent && "bg-gray-100 text-gray-500 border border-gray-200",
          ].filter(Boolean).join(" ");

          const titleCls = [
            "text-sm sm:text-base font-medium leading-tight",
            isCompleted && "text-emerald-800",
            isCurrent && "text-[#0F1F3D]",
            !isCompleted && !isCurrent && "text-gray-700",
          ].filter(Boolean).join(" ");

          return (
            <li key={step.n}>
              <button
                type="button"
                onClick={() => navigate(step.path)}
                className={containerCls}
                data-testid={`workflow-step-${step.n}`}
                data-status={status}
              >
                <span className={badgeCls} aria-hidden="true">
                  {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : step.n}
                </span>

                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className={titleCls} data-testid={`workflow-step-${step.n}-title`}>
                      {isCompleted ? step.completedTitle : step.actionTitle}
                    </span>
                    {isCurrent && (
                      <span
                        className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
                        data-testid={`workflow-step-${step.n}-current-tag`}
                      >
                        Aktualny krok
                      </span>
                    )}
                  </span>
                  <span className="block text-xs sm:text-sm text-gray-500 mt-1">
                    {step.description}
                  </span>
                </span>

                <ArrowRight
                  className={`w-4 h-4 shrink-0 mt-2 ${isCurrent ? "text-blue-600" : "text-gray-300 group-hover:text-gray-500"}`}
                  aria-hidden="true"
                />
                {/* Ukryty marker dla pustego kroku — utrzymuje minimalny wymiar ikony */}
                {!isCompleted && !isCurrent && (
                  <Circle className="hidden" aria-hidden="true" />
                )}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default DashboardPage;
